import { useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { categoryData } from '../redux/slices/categorySlice';
import { safeGetCategories, safeGetCName, safeRenderCategoryList } from '../helper/dataSanitizer';

/**
 * Custom hook for safe category data access
 * Automatically sanitizes category data and provides safe methods
 */
export const useSafeCategories = () => {
  const rawCategories = useSelector(categoryData);

  // Memoize sanitized categories to prevent unnecessary re-renders
  const categories = useMemo(() => {
    return safeGetCategories(rawCategories);
  }, [rawCategories]);

  // Safe category name getter
  const getCategoryName = item => {
    return safeGetCName(item);
  };

  // Safe category list renderer - memoized
  const renderCategoryList = useCallback(
    renderFunction => {
      return safeRenderCategoryList(categories, renderFunction);
    },
    [categories]
  );

  // Safe category filterer - memoized
  const filterCategories = useCallback(
    predicate => {
      return categories.filter(predicate);
    },
    [categories]
  );

  // Safe category finder - memoized
  const findCategory = useCallback(
    predicate => {
      return categories.find(predicate) || null;
    },
    [categories]
  );

  // Safe category mapper - memoized to prevent infinite loops
  const mapCategories = useCallback(
    callback => {
      return categories.map(callback);
    },
    [categories]
  );

  // Safe category slice
  const sliceCategories = (start, end) => {
    return safeGetCategories(categories.slice(start, end));
  };

  // Safe category length
  const categoryCount = categories.length;

  // Safe category access by index
  const getCategoryByIndex = index => {
    return categories[index] || null;
  };

  // Safe category children access
  const getCategoryChildren = category => {
    if (!category || !category.children) {
      return [];
    }
    return safeGetCategories(category.children);
  };

  return {
    categories,
    getCategoryName,
    renderCategoryList,
    filterCategories,
    findCategory,
    mapCategories,
    sliceCategories,
    categoryCount,
    getCategoryByIndex,
    getCategoryChildren,
    // Raw access for advanced use cases
    rawCategories: categories,
  };
};

export default useSafeCategories;
